import { useEffect, useRef, useState } from "react";
import { StickyNote, Check, X } from "lucide-react";
import { saveNote } from "@/lib/notes";

export default function NoteEditor({ questionId, initialText, onChange }) {
  const [open, setOpen] = useState(Boolean(initialText));
  const [text, setText] = useState(initialText || "");
  const [savedAt, setSavedAt] = useState(initialText ? Date.now() : null);
  const debounceRef = useRef(null);

  useEffect(() => {
    setText(initialText || "");
  }, [initialText]);

  // Debounced autosave
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      saveNote(questionId, text);
      onChange?.(questionId, text);
      setSavedAt(Date.now());
    }, 500);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, open, questionId]);

  const clear = () => {
    setText("");
    saveNote(questionId, "");
    onChange?.(questionId, "");
    setSavedAt(null);
  };

  if (!open) {
    return (
      <button
        data-testid={`open-note-${questionId}`}
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 border border-zinc-700 hover:border-amber-500/50 hover:text-amber-400 px-4 py-2 text-sm transition-colors uppercase tracking-wider text-zinc-300"
      >
        <StickyNote className="w-4 h-4" />
        Ajouter une note
      </button>
    );
  }

  return (
    <div
      data-testid={`note-editor-${questionId}`}
      className="mt-3 bg-amber-500/5 border border-amber-500/30 p-4 fade-up"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-amber-400">
          <StickyNote className="w-3.5 h-3.5" />
          Note personnelle
        </div>
        <div className="flex items-center gap-3">
          {savedAt && (
            <span className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1">
              <Check className="w-3 h-3" /> Enregistré
            </span>
          )}
          <button
            data-testid={`delete-note-${questionId}`}
            onClick={clear}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-red-400 transition-colors inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Effacer
          </button>
        </div>
      </div>
      <textarea
        data-testid={`note-textarea-${questionId}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Tes mots-clés, exemples concrets, outils à citer, anecdotes du projet…"
        rows={4}
        className="w-full bg-transparent border-0 focus:outline-none text-sm text-zinc-100 placeholder:text-zinc-600 font-mono leading-relaxed resize-y"
      />
      <div className="text-[10px] uppercase tracking-widest text-zinc-600 mt-1">
        {text.length} caractères · auto-sauvegarde locale
      </div>
    </div>
  );
}
